/**
 * External dependencies
 */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Box, Stack, Heading, Text, Divider } from '@towersystems/towersystems-design-system';
import page from 'page';
import { map, size, get, find } from 'lodash';

/**
 * Internal dependencies
 */
import Main from 'components/main';
import Navigation from 'modules/dashboard/connections/navigation';
import { requestConnection } from 'state/connections/actions';
import { requestLocations } from 'state/locations/actions';
import { requestLocationGroups } from 'state/location-groups/actions';
import { requestLocationWebsites } from 'state/location-websites/actions';
import LoadingPlaceholder from 'modules/dashboard/connections/loading-placeholder';
import EditLocationGroupModal from 'blocks/edit-location-group-modal';
import EditLocationWebsiteModal from 'blocks/edit-location-website-modal';
import { startEditingLocationGroup } from 'state/ui/location-group-modal/actions';
import { startEditingLocationWebsite } from 'state/ui/location-website-modal/actions';
import { getConnectionGroups } from 'state/location-groups/selectors';
import { getConnectionWebsites } from 'state/location-websites/selectors';
import { getAllConnectionTypes } from 'state/connection-types/selectors';
import { requestConnectionLocations } from 'state/connection-locations/actions';
import DefaultRender from './default-render';
import RoamRender from './roam-render';
import GunTradersRender from './gun-traders-render';
/**
 * Style dependencies
 */
import './styles.scss';

const Locations = ({ connectionId, locationGroups, locationWebsites, connectionTypes, ...props }) => {
	const [requesting, setRequesting] = useState(true);
	const [locations, setLocations] = useState([]);
	const [connectionLocations, setConnectionLocations] = useState([]);

	const [connection, setConnection] = useState(null);

	useEffect(() => {
		loadData(connectionId);
	}, []);

	const loadData = async (connectionId) => {
		setRequesting(true);
		try {
			let connection = await props.requestConnection(connectionId);
			let locations = await props.requestLocations();
			let connectionLocations = await props.requestConnectionLocations(connectionId);

			await props.requestLocationGroups({
				criteria: { connection: connectionId },
				limit: 9999,
			});

			await props.requestLocationWebsites({
				criteria: { connection: connectionId },
				limit: 9999,
			});

			setConnection(connection);
			setLocations(locations);
			setConnectionLocations(connectionLocations);
		} catch (error) {
			console.log('error', error);
			page('/');
		}

		setRequesting(false);
	};

	if (true === requesting) {
		return <LoadingPlaceholder connectionId={connectionId} section='locations' />;
	}

	const disabled =
		get(connection, ['connection_type', 'config', 'multiLocationType']) == 'single' && locationGroups.length >= 1;
	const disabledExternalLocation =
		get(connection, ['connection_type', 'config', 'multiLocationType']) == 'single' ? true : false;

	const isShown = disabledExternalLocation ? 'none' : 'block';

	const connectionType = get(connection, ['connection_type', 'code']);

	const render = (connectionType) => {
		switch (connectionType) {
			case 'roam':
				return <RoamRender />;
			case 'gun_traders':
				return <GunTradersRender />;
			default:
				return (
					<DefaultRender
						connectionType={connectionType}
						locations={locations}
						isShow={isShown}
						disabled={disabled}
						locationWebsites={locationWebsites}
						connectionId={connectionId}
						locationGroups={locationGroups}
						{...props}
					/>
				);
		}
	};

	return (
		<Main>
			<Navigation connectionId={connectionId} section='locations' />
			<Box paddingTop='xxlarge' paddingBottom='xxlarge'>
				<Box display='flex' justifyContent='spaceBetween' alignItems='center' paddingBottom='xlarge'>
					<Heading level='1'>Locations</Heading>
				</Box>
				<Divider />
			</Box>
			{render(connectionType)}
			<EditLocationGroupModal
				connectionLocations={connectionLocations}
				connectionId={connectionId}
				disabled={disabled}
				disabledExternalLocation={disabledExternalLocation}
			/>
			<EditLocationWebsiteModal
				connectionLocations={connectionLocations}
				connectionId={connectionId}
				disabled={disabled}
				disabledExternalLocation={disabledExternalLocation}
			/>
		</Main>
	);
};

export default connect(
	(state, ownProps) => ({
		locationGroups: getConnectionGroups(state, ownProps.connectionId),
		connectionTypes: getAllConnectionTypes(state),
		locationWebsites: getConnectionWebsites(state, ownProps.connectionId),
	}),
	{
		requestLocations,
		requestConnection,
		requestLocationGroups,
		startEditingLocationGroup,
		requestConnectionLocations,
		requestLocationWebsites,
		startEditingLocationWebsite,
	},
)(Locations);
